import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { MapPin, Plus, Globe, Loader2, ChevronDown, Check } from 'lucide-react';

export default function Locations() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showExpandModal, setShowExpandModal] = useState(false);
  const [newLocation, setNewLocation] = useState({ city: '', state: '', country: 'USA' });

  const { data: locations, isLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations
  });

  const { data: availableStates } = useQuery({
    queryKey: ['availableStates'],
    queryFn: api.getAvailableStates
  });

  const addLocationMutation = useMutation({
    mutationFn: (location: { city: string; state: string; country: string }) =>
      api.addLocation(location),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setShowAddForm(false);
      setNewLocation({ city: '', state: '', country: 'USA' });
    }
  });

  const expandStateMutation = useMutation({
    mutationFn: (state: string) => api.expandToState(state, 'USA'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setShowExpandModal(false);
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          <p className="text-gray-500">Manage locations for your answer content</p>
        </div>
        <div className="flex space-x-3">
          <button onClick={() => setShowExpandModal(true)} className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50">
            <Globe className="h-4 w-4 mr-2" />
            Expand to State
          </button>
          <button onClick={() => setShowAddForm(true)} className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </button>
        </div>
      </div>

      {/* Add Location Form */}
      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Location</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addLocationMutation.mutate(newLocation);
            }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={newLocation.city}
                onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Boston"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                value={newLocation.state}
                onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Massachusetts"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                value={newLocation.country}
                onChange={(e) => setNewLocation({ ...newLocation, country: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="USA"
              />
            </div>
            <div className="sm:col-span-3 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addLocationMutation.isPending}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
              >
                {addLocationMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Location'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Locations Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {locations?.map((location: any) => (
          <div key={location.id} className="card hover:shadow-lg transition-shadow">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MapPin className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900">
                  {location.city}, {location.state}
                </h3>
                <p className="text-sm text-gray-500">{location.country}</p>
                <p className="text-xs text-gray-400 mt-1">/{location.slug}/</p>
              </div>
              {location.is_primary && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Primary</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Expand to State Modal */}
      {showExpandModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Expand to All Cities in a State
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Select a state to automatically add all major cities as locations.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableStates?.map((state: any) => (
                <button
                  key={state.abbreviation}
                  onClick={() => expandStateMutation.mutate(state.name)}
                  disabled={expandStateMutation.isPending}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <span className="font-medium text-gray-900">{state.name}</span>
                  <span className="text-sm text-gray-500">{state.cityCount} cities</span>
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowExpandModal(false)}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
            {expandStateMutation.isPending && (
              <div className="mt-4 flex items-center justify-center text-blue-600">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Adding cities...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {locations?.length === 0 && (
        <div className="card text-center py-12">
          <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No locations yet</h3>
          <p className="text-gray-500 mb-4">Add your first location to start generating content</p>
          <button onClick={() => setShowAddForm(true)} className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add First Location
          </button>
        </div>
      )}
    </div>
  );
}
